import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { Button, Card, Input, Select, Spinner, Badge, fmtBRL, fmtNum } from "@/components/ui/Primitives";
import { api, formatErr } from "@/lib/api";
import { ArrowLeft, Save, ChevronDown, ChevronRight, Trash2, AlertCircle, Split, Plus, X } from "lucide-react";

const CATS = [
  { k: "cx1", label: "CX1" },
  { k: "cx2", label: "CX2" },
  { k: "cx3", label: "CX3" },
  { k: "cx3g", label: "CX3G" },
  { k: "cx4", label: "CX4" },
];

const TIPOS_CEBOLA = [
  { k: "amarela", label: "Amarela", dot: "bg-yellow-400" },
  { k: "roxa", label: "Roxa", dot: "bg-purple-500" },
  { k: "branca", label: "Branca", dot: "bg-white border border-ink-300" },
  { k: "pelada", label: "Pelada", dot: "bg-orange-200 border border-orange-400" },
];

const emptyPrecos = () => Object.fromEntries(
  CATS.map((c) => [c.k, Object.fromEntries(TIPOS_CEBOLA.map((t) => [t.k, ""]))])
);
const emptyItem = (invoice) => ({
  invoice_id: invoice.invoice_id,
  nota_numero: invoice.nota_numero,
  fornecedor: invoice.fornecedor,
  tipo_cebola: invoice.tipo_cebola,
  available: invoice.available,
  custos: invoice.custos || { cx1: 0, cx2: 0, cx3: 0, cx3g: 0, cx4: 0 },
  expanded: true,
  cx1: { quantidade: "" },
  cx2: { quantidade: "" },
  cx3: { quantidade: "" },
  cx3g: { quantidade: "" },
  cx4: { quantidade: "" },
});

export default function ShipmentForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [shipment, setShipment] = useState(null); // dados originais para verificar status pendente
  const [clientes, setClientes] = useState([]);
  const [available, setAvailable] = useState([]);
  const [form, setForm] = useState({
    cliente_id: "", destino: "", motorista: "", placa: "", nf_saida_numero: "",
    frete_tipo: "FOB", valor_frete: "", descarga: "", seguro: "", desconto_pct: "",
    descarga_mode: "saco",
    // Descarga já vem marcada como AUMENTAR (padrão: repassar custo ao cliente por saco).
    frete_op: "sub", descarga_op: "add", seguro_op: "sub", desconto_op: "sub",
    vendedor: "", comissao_pct: "",
    data_vencimento: "", observacoes: "",
    precos: emptyPrecos(),
    // precos_splits[cat][tipo] = [{quantidade, preco, descricao}]
    // Quando existe, substitui o preço-base no cálculo e emite 1 linha NF-e por split
    precos_splits: {},
    selectedItems: [], // [{invoice_id, available, qtds, sacarias...}]
  });

  const estornarBaixa = async (o) => {
    if (!window.confirm(`A NF #${o.nota} ${o.label} está sem saldo, provavelmente por uma baixa manual (Reconciliar/Zerar estoque).\n\nDevolver ao estoque desta NF as caixas necessárias para finalizar a saída?\n(Perdas de rebeneficiamento não são devolvidas.)`)) return;
    try {
      const { data } = await api.post(`/invoices/${o.invoice_id}/estornar-baixa`, { categoria: o.cat, pedido: o.pedido, exclude_shipment_id: isEdit ? id : undefined });
      setForm((f) => ({ ...f, selectedItems: f.selectedItems.map((it) => it.invoice_id !== o.invoice_id ? it
        : { ...it, available: { ...(it.available || {}), [o.cat]: data.disponivel ?? ((Number(it.available?.[o.cat]) || 0) + data.estornado) } }) }));
      alert(`✅ ${data.estornado} cx devolvidas ao estoque da NF #${o.nota}.`);
    } catch (e) { alert(e?.response?.data?.detail || "Erro ao devolver ao estoque"); }
  };

  useEffect(() => {
    Promise.all([
      api.get("/clientes"),
      api.get("/stock/available", { params: isEdit ? { exclude_shipment_id: id } : {} }),
      isEdit ? api.get(`/shipments/${id}`) : Promise.resolve(null),
    ]).then(([cs, av, sh]) => {
      setClientes(cs.data || []);
      setAvailable(av.data || []);
      if (sh && sh.data) {
        const s = sh.data;
        setShipment(s);
        // Reconstroi precos por (categoria, tipo_cebola), com fallback ao modelo legado
        const precos = {};
        CATS.forEach((c) => {
          const src = s.precos?.[c.k] || {};
          const legacy = src.generica ?? src.serenata ?? src.unico ?? "";
          precos[c.k] = Object.fromEntries(TIPOS_CEBOLA.map((t) => [
            t.k, (src[t.k] !== undefined && src[t.k] !== null && src[t.k] !== "") ? src[t.k] : legacy,
          ]));
        });
        // Preço granel (R$/kg): carrega valor único ou por tipo_cebola
        const src_g = s.precos?.granel || {};
        precos.granel = {
          bruta: src_g.bruta ?? src_g.unico ?? "",
        };
        const sel = (s.itens || []).map((it) => {
          const av_match = (av.data || []).find((a) => a.invoice_id === it.invoice_id);
          return {
            invoice_id: it.invoice_id,
            nota_numero: it.nota_numero,
            fornecedor: it.fornecedor,
            tipo_cebola: it.tipo_cebola,
            available: av_match?.available || { cx1:0, cx2:0, cx3:0, cx3g:0, cx4:0 },
            custos: it._custos || av_match?.custos || { cx1:0, cx2:0, cx3:0, cx3g:0, cx4:0 },
            expanded: true,
            ...Object.fromEntries(CATS.map((c) => [c.k, {
              quantidade: it[c.k]?.quantidade ?? "",
            }])),
            // Granel: quando a NF é "Cebola Bruta", a saída carrega peso em kg em vez de caixas
            granel: it.granel ? { quantidade: Number(it.granel.quantidade) || 0, unidade: it.granel.unidade || "kg" } : null,
          };
        });
        setForm({
          cliente_id: s.cliente_id || "",
          destino: s.destino || "", motorista: s.motorista || "", placa: s.placa || "", nf_saida_numero: s.nf_saida_numero || "",
          frete_tipo: s.frete_tipo || "FOB",
          valor_frete: s.valor_frete ?? "",
          descarga: s.descarga ?? "",
          seguro: s.seguro ?? "",
          desconto_pct: s.desconto_pct ?? "",
          descarga_mode: s.descarga_mode || "total",  // legado: sem campo => total
          frete_op: s.frete_op || "sub",
          descarga_op: s.descarga_op || "sub",
          seguro_op: s.seguro_op || "sub",
          desconto_op: s.desconto_op || "sub",
          vendedor: s.vendedor || "",
          comissao_pct: s.comissao_pct ?? "",
          data_vencimento: s.data_vencimento || "",
          observacoes: s.observacoes || "",
          precos, selectedItems: sel,
          precos_splits: s.precos_splits || {},
        });
      }
    }).catch((e) => setErr(formatErr(e))).finally(() => setLoading(false));
  }, [id, isEdit]);

  // Prefill legado /expedicao (mantido apenas para retrocompatibilidade — novo fluxo usa /shipments/from-expedicao)
  useEffect(() => {
    if (isEdit) return;
    const search = new URLSearchParams(window.location.search);
    if (search.get("fromExpedicao") !== "1") return;
    if (available.length === 0) return;
    try {
      const raw = sessionStorage.getItem("expedicao_prefill");
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!parsed?.items?.length) return;
      const sel = parsed.items.map((it) => {
        const av = available.find((a) => a.invoice_id === it.invoice_id);
        return {
          invoice_id: it.invoice_id,
          nota_numero: it.nota_numero || av?.nota_numero || "",
          fornecedor: it.fornecedor || av?.fornecedor || "",
          tipo_cebola: it.tipo_cebola || av?.tipo_cebola || "amarela",
          available: av?.available || { cx1: 0, cx2: 0, cx3: 0, cx3g: 0, cx4: 0 },
          custos: av?.custos || { cx1: 0, cx2: 0, cx3: 0, cx3g: 0, cx4: 0 },
          expanded: true,
          ...Object.fromEntries(CATS.map((c) => [c.k, {
            quantidade: it[c.k]?.quantidade || "",
          }])),
        };
      });
      setForm((f) => ({ ...f, selectedItems: sel }));
      sessionStorage.removeItem("expedicao_prefill");
    } catch (_) { /* ignore */ }
  }, [isEdit, available]);

  const addInvoice = (av) => {
    if (form.selectedItems.find((s) => s.invoice_id === av.invoice_id)) return;
    setForm((f) => ({ ...f, selectedItems: [...f.selectedItems, emptyItem(av)] }));
  };
  const removeInvoice = (invId) => {
    setForm((f) => ({ ...f, selectedItems: f.selectedItems.filter((s) => s.invoice_id !== invId) }));
  };
  const setItem = (invId, cat, field, val) => {
    setForm((f) => ({
      ...f,
      selectedItems: f.selectedItems.map((s) =>
        s.invoice_id === invId ? { ...s, [cat]: { ...s[cat], [field]: val } } : s
      ),
    }));
  };
  const togglePanel = (invId) => {
    setForm((f) => ({
      ...f,
      selectedItems: f.selectedItems.map((s) => s.invoice_id === invId ? { ...s, expanded: !s.expanded } : s),
    }));
  };
  const setPreco = (cat, tipo, val) => {
    setForm((f) => ({ ...f, precos: { ...f.precos, [cat]: { ...f.precos[cat], [tipo]: val } } }));
  };

  // ----- Splits (divisões de preço) -----
  const getSplits = (cat, tipo) => form.precos_splits?.[cat]?.[tipo] || [];
  const hasSplits = (cat, tipo) => (getSplits(cat, tipo).length > 0);
  const setSplits = (cat, tipo, arr) => {
    setForm((f) => {
      const cur = f.precos_splits || {};
      const byCat = { ...(cur[cat] || {}) };
      if (arr.length === 0) {
        delete byCat[tipo];
      } else {
        byCat[tipo] = arr;
      }
      const nextCat = { ...cur };
      if (Object.keys(byCat).length === 0) delete nextCat[cat];
      else nextCat[cat] = byCat;
      return { ...f, precos_splits: nextCat };
    });
  };
  const addSplit = (cat, tipo) => setSplits(cat, tipo, [...getSplits(cat, tipo), { quantidade: "", preco: "", descricao: "" }]);
  const updateSplit = (cat, tipo, idx, field, val) => {
    const arr = getSplits(cat, tipo).map((s, i) => i === idx ? { ...s, [field]: val } : s);
    setSplits(cat, tipo, arr);
  };
  const removeSplit = (cat, tipo, idx) => {
    const arr = getSplits(cat, tipo).filter((_, i) => i !== idx);
    setSplits(cat, tipo, arr);
  };

  // Tipos de cebola presentes nas NFs selecionadas (para exibir só o que interessa)
  const tiposPresentes = useMemo(() => {
    const set = new Set();
    for (const it of form.selectedItems) {
      const t = (it.tipo_cebola || "amarela").toLowerCase();
      set.add(t);
    }
    // Ordena conforme TIPOS_CEBOLA
    return TIPOS_CEBOLA.filter((t) => set.has(t.k));
  }, [form.selectedItems]);

  const qtdPorCatTipo = useMemo(() => {
    const m = {};
    for (const c of CATS) {
      m[c.k] = {};
      for (const t of TIPOS_CEBOLA) m[c.k][t.k] = 0;
    }
    for (const it of form.selectedItems) {
      const tipo = (it.tipo_cebola || "amarela").toLowerCase();
      for (const c of CATS) {
        m[c.k][tipo] += Number(it[c.k]?.quantidade) || 0;
      }
    }
    return m;
  }, [form.selectedItems]);

  // Custo médio ponderado por (categoria, tipo_cebola).
  // Peso = quantidade digitada de cada NF. Fallback: se nada digitado, usa média simples
  // das NFs daquele tipo (para exibir o custo antes do usuário digitar quantidades).
  const custoPorCatTipo = useMemo(() => {
    const m = {};
    for (const c of CATS) {
      m[c.k] = {};
      for (const t of TIPOS_CEBOLA) m[c.k][t.k] = 0;
    }
    for (const c of CATS) {
      for (const t of TIPOS_CEBOLA) {
        let totalQ = 0, totalCost = 0;
        let simpleSum = 0, simpleN = 0;
        for (const it of form.selectedItems) {
          if ((it.tipo_cebola || "amarela").toLowerCase() !== t.k) continue;
          const cost = Number(it.custos?.[c.k]) || 0;
          if (cost <= 0) continue;
          simpleSum += cost; simpleN += 1;
          const q = Number(it[c.k]?.quantidade) || 0;
          if (q > 0) { totalQ += q; totalCost += q * cost; }
        }
        m[c.k][t.k] = totalQ > 0 ? (totalCost / totalQ) : (simpleN > 0 ? simpleSum / simpleN : 0);
      }
    }
    return m;
  }, [form.selectedItems]);

  const calc = useMemo(() => {
    let valor_bruto = 0, total_caixas = 0;
    let cmv_total = 0;
    const overflows = [];
    const prejuizos = [];
    // Ajustes de splits: contribuição do preço-base já foi somada; se (cat, tipo) tem splits,
    // remove-a e adiciona a soma dos splits.
    const splitAdjustments = []; // {cat, tipo, valor_base_removido, valor_splits_adicionado}
    for (const it of form.selectedItems) {
      const tipo = (it.tipo_cebola || "amarela").toLowerCase();
      for (const c of CATS) {
        const q = Number(it[c.k].quantidade) || 0;
        const preco = Number(form.precos[c.k]?.[tipo]) || 0;
        const custo = Number(it.custos?.[c.k]) || 0;
        valor_bruto += q * preco;
        cmv_total += q * custo;
        total_caixas += q;
        if (q > (it.available?.[c.k] || 0)) {
          overflows.push({ text: `NF #${it.nota_numero} ${c.label}: pedido ${q} > disp ${it.available?.[c.k] || 0}`, invoice_id: it.invoice_id, nota: it.nota_numero, cat: c.k, label: c.label, falta: q - (it.available?.[c.k] || 0), pedido: q });
        }
        if (q > 0 && preco > 0 && custo > 0 && preco < custo) {
          const tipoLabel = TIPOS_CEBOLA.find((tt) => tt.k === tipo)?.label || tipo;
          prejuizos.push(`${tipoLabel} ${c.label}: venda ${fmtBRL(preco)} < custo ${fmtBRL(custo)}`);
        }
      }
      // Granel: soma peso × preço/kg
      if (it.granel && it.granel.quantidade > 0) {
        const kg = Number(it.granel.quantidade) || 0;
        const precoKg = Number(form.precos.granel?.bruta) || 0;
        valor_bruto += kg * precoKg;
      }
    }
    // Aplica splits: substitui a contribuição base pelas divisões
    const splitsMeta = form.precos_splits || {};
    const splitsWarnings = [];
    for (const c of CATS) {
      for (const t of TIPOS_CEBOLA) {
        const arr = splitsMeta[c.k]?.[t.k];
        if (!arr || arr.length === 0) continue;
        // Qtd total base para (c, t)
        let qtdBase = 0;
        for (const it of form.selectedItems) {
          if ((it.tipo_cebola || "amarela").toLowerCase() !== t.k) continue;
          qtdBase += Number(it[c.k].quantidade) || 0;
        }
        const precoBase = Number(form.precos[c.k]?.[t.k]) || 0;
        const valorBaseOriginal = qtdBase * precoBase;
        const valorSplits = arr.reduce((a, s) => a + (Number(s.quantidade) || 0) * (Number(s.preco) || 0), 0);
        const qtdSplits = arr.reduce((a, s) => a + (Number(s.quantidade) || 0), 0);
        valor_bruto = valor_bruto - valorBaseOriginal + valorSplits;
        // Warnings
        if (Math.abs(qtdSplits - qtdBase) > 0.001) {
          splitsWarnings.push({
            cat: c.k, tipo: t.k, label: `${TIPOS_CEBOLA.find(tt=>tt.k===t.k)?.label} ${c.label}`,
            qtdBase, qtdSplits, diff: qtdBase - qtdSplits,
          });
        }
        splitAdjustments.push({ cat: c.k, tipo: t.k, qtdBase, qtdSplits, valorSplits });
      }
    }
    const desc_pct = Number(form.desconto_pct) || 0;
    const desconto = valor_bruto * desc_pct / 100;
    const frete = Number(form.valor_frete) || 0;
    const descarga_input = Number(form.descarga) || 0;
    // Descarga em modo "saco" → total = valor × total_caixas; modo "total" → valor puro.
    const descarga = form.descarga_mode === "saco" ? descarga_input * total_caixas : descarga_input;
    const seguro = Number(form.seguro) || 0;
    const comissao_pct = Number(form.comissao_pct) || 0;
    const comissao = valor_bruto * comissao_pct / 100;
    const sig = (op) => op === "add" ? 1 : -1;
    const sig_desc = sig(form.desconto_op);
    const sig_frete = sig(form.frete_op);
    const sig_descarga = sig(form.descarga_op);
    const sig_seguro = sig(form.seguro_op);
    let liquido = valor_bruto
      + sig_desc * desconto
      + sig_descarga * descarga
      + sig_seguro * seguro
      - comissao;
    if (form.frete_tipo === "CIF") liquido += sig_frete * frete;
    const margem_bruta = valor_bruto - cmv_total;
    // Fator multiplicativo do desconto (%) e valor absoluto adicionado/subtraído por saco.
    // Frete e seguro sempre são rateio de valor total; descarga depende do modo.
    const fator_pct = 1 + sig_desc * (desc_pct / 100);
    const descarga_per_sc = form.descarga_mode === "saco" ? descarga_input : (total_caixas > 0 ? descarga_input / total_caixas : 0);
    const add_per_sc =
      sig_descarga * descarga_per_sc
      + (total_caixas > 0 ? (sig_seguro * seguro + (form.frete_tipo === "CIF" ? sig_frete * frete : 0)) / total_caixas : 0);
    return { valor_bruto, total_caixas, desconto, comissao, liquido, overflows, cmv_total, margem_bruta, prejuizos,
             sig_desc, sig_frete, sig_descarga, sig_seguro,
             fator_pct, add_per_sc, descarga_total: descarga,
             splitsWarnings };
  }, [form]);

  const buildPayload = () => ({
    cliente_id: form.cliente_id,
    destino: form.destino, motorista: form.motorista, placa: form.placa, nf_saida_numero: form.nf_saida_numero,
    frete_tipo: form.frete_tipo,
    valor_frete: Number(form.valor_frete) || 0,
    descarga: Number(form.descarga) || 0,
    seguro: Number(form.seguro) || 0,
    desconto_pct: Number(form.desconto_pct) || 0,
    descarga_mode: form.descarga_mode || "saco",
    frete_op: form.frete_op || "sub",
    descarga_op: form.descarga_op || "sub",
    seguro_op: form.seguro_op || "sub",
    desconto_op: form.desconto_op || "sub",
    vendedor: form.vendedor || "",
    comissao_pct: Number(form.comissao_pct) || 0,
    data_vencimento: form.data_vencimento || "",
    observacoes: form.observacoes || "",
    // Envia precos por (categoria, tipo_cebola). Também popula generica/serenata (legado)
    // com o valor de "amarela" (default) para retrocompatibilidade em relatórios antigos.
    precos: {
      ...Object.fromEntries(CATS.map((c) => {
        const byTipo = Object.fromEntries(TIPOS_CEBOLA.map((t) => [t.k, Number(form.precos[c.k]?.[t.k]) || 0]));
        return [c.k, { ...byTipo, generica: byTipo.amarela, serenata: byTipo.amarela }];
      })),
      // Preço granel (R$/kg) — usado apenas para itens com bloco granel
      granel: { bruta: Number(form.precos.granel?.bruta) || 0 },
    },
    // Divisões de preço por (cat, tipo). Só envia entries com quantidade > 0 E preco > 0.
    precos_splits: (() => {
      const out = {};
      for (const c of CATS) {
        const byTipo = {};
        for (const t of TIPOS_CEBOLA) {
          const arr = form.precos_splits?.[c.k]?.[t.k] || [];
          const clean = arr
            .map((s) => ({
              quantidade: Number(s.quantidade) || 0,
              preco: Number(s.preco) || 0,
              descricao: (s.descricao || "").trim(),
            }))
            .filter((s) => s.quantidade > 0 && s.preco > 0);
          if (clean.length > 0) byTipo[t.k] = clean;
        }
        if (Object.keys(byTipo).length > 0) out[c.k] = byTipo;
      }
      return out;
    })(),
    itens: form.selectedItems.map((it) => ({
      invoice_id: it.invoice_id,
      ...Object.fromEntries(CATS.map((c) => [c.k, {
        quantidade: Number(it[c.k].quantidade) || 0,
        sacaria_tipo: "Genérica",
      }])),
      // Preserva bloco granel quando NF é "Cebola Bruta"
      ...(it.granel && it.granel.quantidade > 0
        ? { granel: { quantidade: Number(it.granel.quantidade), unidade: it.granel.unidade || "kg" } }
        : {}),
    })),
  });

  const hasAnyPrice = () => CATS.some((c) => TIPOS_CEBOLA.some((t) => Number(form.precos[c.k]?.[t.k]) > 0))
    || Number(form.precos.granel?.bruta) > 0;
  const isPendente = isEdit && shipment?.shipment_status === "pendente";

  const save = async () => {
    if (!form.cliente_id) { setErr("Selecione um cliente."); return; }
    if (form.selectedItems.length === 0) { setErr("Adicione pelo menos uma NF."); return; }
    setBusy(true); setErr("");
    try {
      const payload = buildPayload();
      const { data } = isEdit
        ? await api.put(`/shipments/${id}`, payload)
        : await api.post("/shipments", payload);
      // Se era pendente e agora tem cliente + pelo menos um preço, finaliza automaticamente
      if (isPendente && hasAnyPrice()) {
        try {
          await api.post(`/shipments/${data.id}/finalize`);
        } catch (e) {
          setErr(formatErr(e));
          setBusy(false);
          return;
        }
      }
      nav(`/shipments/${data.id}`);
    } catch (e) { setErr(formatErr(e)); }
    finally { setBusy(false); }
  };

  if (loading) return <Layout title="Nova Saída"><div className="flex justify-center py-20"><Spinner size={28} /></div></Layout>;

  return (
    <Layout
      title={isPendente ? "Preencher Saída Pendente" : (isEdit ? "Editar Saída" : "Nova Saída")}
      subtitle={isPendente ? "Adicione cliente e preços para finalizar" : "Romaneio de venda — selecione NFs em estoque"}
      actions={<Button variant="secondary" onClick={() => nav("/shipments")}><ArrowLeft size={16} /> Voltar</Button>}
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Cliente + transporte */}
          <Card className="p-6">
            <h3 className="font-heading text-lg font-bold text-ink-900 mb-4">Cliente & Transporte</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select label="Cliente" required value={form.cliente_id} onChange={(e) => {
                const cid = e.target.value;
                const cli = clientes.find((c) => c.id === cid);
                const prazo = Number(cli?.prazo_faturamento_dias || 0);
                // Auto-preenche data_vencimento com hoje + prazo do cliente (se ainda não foi editado manualmente)
                let venc = form.data_vencimento;
                if (prazo > 0) {
                  const d = new Date();
                  d.setDate(d.getDate() + prazo);
                  venc = d.toISOString().slice(0, 10);
                }
                setForm({ ...form, cliente_id: cid, data_vencimento: venc });
              }} data-testid="shipment-cliente">
                <option value="">Selecione...</option>
                {clientes.map((c) => <option key={c.id} value={c.id}>{c.nome} {c.cnpj_cpf ? `(${c.cnpj_cpf})` : ""} {c.prazo_faturamento_dias ? `— ${c.prazo_faturamento_dias}d` : ""}</option>)}
              </Select>
              <Input label="Destino (cidade/UF)" value={form.destino} onChange={(e) => setForm({ ...form, destino: e.target.value })} data-testid="shipment-destino" />
              <Input label="Motorista" value={form.motorista} onChange={(e) => setForm({ ...form, motorista: e.target.value })} />
              <Input label="Placa" value={form.placa} onChange={(e) => setForm({ ...form, placa: e.target.value })} />
              <Input label="Nº NF de saída" placeholder="Ex: 1234" value={form.nf_saida_numero} onChange={(e) => setForm({ ...form, nf_saida_numero: e.target.value })} data-testid="shipment-nf-saida" />
            </div>
          </Card>

          {/* Estoque disponível — oculto ao editar (já tem NFs vinculadas) */}
          {!isEdit && (
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-heading text-lg font-bold text-ink-900">NFs disponíveis</h3>
                <p className="text-xs text-ink-500">Clique para adicionar à saída</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-72 overflow-y-auto">
              {available.length === 0 && (
                <div className="col-span-2 text-center py-8 text-sm text-ink-500">Nenhuma NF beneficiada disponível em estoque.</div>
              )}
              {available.map((av) => {
                const used = !!form.selectedItems.find((s) => s.invoice_id === av.invoice_id);
                return (
                  <button
                    key={av.invoice_id}
                    onClick={() => addInvoice(av)}
                    disabled={used}
                    type="button"
                    data-testid={`add-nf-${av.invoice_id}`}
                    className={`text-left p-3 rounded-md border transition-colors ${used ? "border-emerald-300 bg-emerald-50 opacity-60 cursor-not-allowed" : "border-ink-200 hover:border-brand-500 hover:bg-brand-50"}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-semibold">NF #{av.nota_numero}</span>
                      {used ? <Badge tone="success">Adicionada</Badge> : <Badge tone="neutral">{fmtNum(av.total, 0)} cx</Badge>}
                    </div>
                    <div className="text-xs text-ink-500 mt-0.5 truncate">{av.fornecedor} • {av.tipo_cebola}</div>
                    <div className="grid grid-cols-5 gap-1 mt-2 text-[11px] font-mono text-ink-700">
                      {CATS.map((c) => (
                        <div key={c.k} className="text-center">
                          <div className="text-[9px] uppercase text-ink-300">{c.label}</div>
                          <div>{fmtNum(av.available[c.k] || 0, 0)}</div>
                        </div>
                      ))}
                    </div>
                  </button>
                );
              })}
            </div>
          </Card>
          )}

          {/* Itens selecionados — oculto ao editar (edição foca em preços/frete/cliente) */}
          {!isEdit && form.selectedItems.length > 0 && (
            <Card className="p-6">
              <h3 className="font-heading text-lg font-bold text-ink-900 mb-4">Itens da saída</h3>
              <div className="space-y-3">
                {form.selectedItems.map((it) => (
                  <div key={it.invoice_id} className="border border-ink-200 rounded-md">
                    <div className="flex items-center justify-between px-4 py-2.5 bg-stone-50 cursor-pointer" onClick={() => togglePanel(it.invoice_id)}>
                      <div className="flex items-center gap-2">
                        {it.expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        <span className="font-semibold">NF #{it.nota_numero}</span>
                        <span className="text-xs text-ink-500">{it.fornecedor} • {it.tipo_cebola}</span>
                      </div>
                      <button type="button" onClick={(e) => { e.stopPropagation(); removeInvoice(it.invoice_id); }} className="p-1 rounded-md hover:bg-red-100 text-danger" data-testid={`remove-item-${it.invoice_id}`}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                    {it.expanded && (
                      <div className="p-4 space-y-2">
                        {(() => {
                          const hasOver = CATS.some((c) => (Number(it[c.k].quantidade) || 0) > (it.available[c.k] || 0));
                          if (!hasOver) return null;
                          return (
                            <button
                              type="button"
                              onClick={async () => {
                                try {
                                  const r = await api.get(`/stock/available/${it.invoice_id}/debug`);
                                  const s = r.data.saidas_ativas || [];
                                  if (s.length === 0) {
                                    alert("Nenhuma saída ativa consumindo estoque desta NF.\nSe está aparecendo como excedido, é bug de cálculo — reporte.");
                                    return;
                                  }
                                  const lines = s.slice(0, 10).map((x) =>
                                    `• ${x.numero} [${x.status}${x.origem_expedicao ? "-expedição" : ""}] — ${x.cliente || "?"} · vendedor: ${x.vendedor || "?"} · ${x.total_cx} cx`
                                  ).join("\n");
                                  alert(`Saídas que estão consumindo o estoque desta NF:\n\n${lines}\n\nSe alguma é rascunho abandonada, exclua ela pra liberar o estoque.`);
                                } catch (e) {
                                  alert("Erro no diagnóstico: " + (e?.response?.data?.detail || e.message));
                                }
                              }}
                              className="text-xs px-2 py-1 rounded bg-amber-100 border border-amber-300 text-amber-800 hover:bg-amber-200 font-semibold"
                              data-testid={`debug-stock-${it.invoice_id}`}
                            >
                              🔍 Ver o que está segurando este estoque
                            </button>
                          );
                        })()}
                        {CATS.map((c) => {
                          const q = Number(it[c.k].quantidade) || 0;
                          const disp = it.available[c.k] || 0;
                          const over = q > disp;
                          const tipo = (it.tipo_cebola || "amarela").toLowerCase();
                          const precoTipo = Number(form.precos[c.k]?.[tipo]) || 0;
                          return (
                            <div key={c.k} className="grid grid-cols-12 gap-2 items-end">
                              <div className="col-span-12 md:col-span-3 text-xs uppercase tracking-wider text-ink-500 font-bold mb-1 md:mb-0">
                                {c.label} <span className="font-mono normal-case text-ink-300">/ disp {fmtNum(disp, 0)}</span>
                              </div>
                              <div className="col-span-6 md:col-span-4">
                                <input
                                  type="number"
                                  min="0"
                                  placeholder="0"
                                  value={it[c.k].quantidade}
                                  onChange={(e) => setItem(it.invoice_id, c.k, "quantidade", e.target.value)}
                                  className={`w-full rounded-md bg-stone-50 border px-3 py-2 text-sm focus:outline-none focus:ring-2 ${over ? "border-danger focus:ring-red-500" : "border-stone-300 focus:ring-brand-500"} focus:border-transparent`}
                                  data-testid={`item-${it.invoice_id}-${c.k}-qty`}
                                />
                              </div>
                              <div className="col-span-6 md:col-span-5 text-right">
                                {over ? (
                                  <span className="text-xs text-danger flex items-center justify-end gap-1"><AlertCircle size={12} /> Excede disponível</span>
                                ) : (
                                  <span className="text-xs text-ink-500 font-mono">
                                    {fmtBRL(q * precoTipo)}
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Preços */}
          <Card className="p-6">
            <h3 className="font-heading text-lg font-bold text-ink-900 mb-1">Preço efetivo (base)</h3>
            <p className="text-xs text-ink-500 mb-4">
              Digite o valor <span className="font-semibold">efetivo/base</span> por saco. Se houver ajustes (%, descarga…) em modo AUMENTAR, o valor da mercadoria que vai para o romaneio/NF é somado logo abaixo.
              {tiposPresentes.length > 1 ? " Separado por tipo de cebola." : ""}
            </p>
            {tiposPresentes.length === 0 ? (
              <div className="text-sm text-ink-500 italic">Adicione uma NF para definir os preços.</div>
            ) : (
              <div className="space-y-5">
                {tiposPresentes.map((t) => (
                  <div key={t.k} data-testid={`preco-block-${t.k}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`w-3 h-3 rounded-full ${t.dot}`} />
                      <span className="text-sm font-semibold text-ink-900 uppercase tracking-wider">Cebola {t.label}</span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                      {CATS.map((c) => {
                        const qtd = qtdPorCatTipo[c.k]?.[t.k] || 0;
                        const custo = custoPorCatTipo[c.k]?.[t.k] || 0;
                        const preco = Number(form.precos[c.k]?.[t.k]) || 0;
                        const prejuizo = preco > 0 && custo > 0 && preco < custo;
                        // Preço efetivo por saco após aplicar desconto% e rateio de descarga/seguro/frete
                        const preco_efetivo = preco > 0 ? preco * calc.fator_pct + calc.add_per_sc : 0;
                        const temAjuste = preco > 0 && Math.abs(preco_efetivo - preco) > 0.005;
                        const efetivoAcima = preco_efetivo > preco;
                        const splits = getSplits(c.k, t.k);
                        const isSplit = splits.length > 0;
                        const qtdSplits = splits.reduce((a, s) => a + (Number(s.quantidade) || 0), 0);
                        const valorSplits = splits.reduce((a, s) => a + (Number(s.quantidade) || 0) * (Number(s.preco) || 0), 0);
                        const splitDiff = qtd - qtdSplits;
                        return (
                          <div key={c.k} className={isSplit ? "md:col-span-5" : ""}>
                            <div className="flex items-baseline justify-between mb-1">
                              <label className="block text-xs font-bold uppercase tracking-[0.1em] text-ink-500">{c.label}</label>
                              <div className="flex items-center gap-2">
                                <span className={`text-xs font-mono font-bold ${qtd > 0 ? "text-brand-600" : "text-ink-300"}`} data-testid={`qty-${t.k}-${c.k}`}>
                                  {qtd > 0 ? `${fmtNum(qtd, 0)} sc` : "—"}
                                </span>
                                {qtd > 0 && !isSplit && (
                                  <button
                                    type="button"
                                    onClick={() => addSplit(c.k, t.k)}
                                    className="text-[10px] px-1.5 py-0.5 rounded bg-brand-50 text-brand-700 border border-brand-200 hover:bg-brand-100 flex items-center gap-1"
                                    title="Dividir em sub-lotes com preços/descrições diferentes"
                                    data-testid={`split-toggle-${t.k}-${c.k}`}
                                  >
                                    <Split size={10} /> Dividir
                                  </button>
                                )}
                              </div>
                            </div>
                            {isSplit ? (
                              /* MODO SPLIT: mini-editor com sub-lotes */
                              <div className="border-2 border-brand-300 rounded-md bg-brand-50/40 p-3 space-y-2" data-testid={`splits-block-${t.k}-${c.k}`}>
                                <div className="flex items-center justify-between text-xs">
                                  <span className="font-bold text-brand-700 uppercase tracking-wider flex items-center gap-1">
                                    <Split size={12} /> {c.label} dividido em {splits.length} lote{splits.length > 1 ? "s" : ""}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => setSplits(c.k, t.k, [])}
                                    className="text-danger hover:text-red-800 text-[11px] underline"
                                    data-testid={`splits-clear-${t.k}-${c.k}`}
                                  >
                                    Voltar a preço único
                                  </button>
                                </div>
                                <div className="space-y-1.5">
                                  <div className="hidden md:grid md:grid-cols-[80px_1fr_120px_100px_100px_32px] gap-2 text-[10px] font-bold uppercase tracking-wider text-ink-500 px-1">
                                    <div>Qtd (sc)</div>
                                    <div>Descrição (aparece na NF-e)</div>
                                    <div>Preço R$/sc</div>
                                    <div className="text-right">Subtotal</div>
                                    <div className="text-right">Ajustado</div>
                                    <div></div>
                                  </div>
                                  {splits.map((s, idx) => {
                                    const qS = Number(s.quantidade) || 0;
                                    const pS = Number(s.preco) || 0;
                                    const sub = qS * pS;
                                    const efet = pS > 0 ? pS * calc.fator_pct + calc.add_per_sc : 0;
                                    return (
                                      <div key={idx} className="grid grid-cols-2 md:grid-cols-[80px_1fr_120px_100px_100px_32px] gap-2 items-center">
                                        <input
                                          type="number" step="1" min="0" placeholder="0"
                                          value={s.quantidade}
                                          onChange={(e) => updateSplit(c.k, t.k, idx, "quantidade", e.target.value)}
                                          className="rounded-md bg-white border border-stone-300 px-2 py-1.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-500"
                                          data-testid={`split-qtd-${t.k}-${c.k}-${idx}`}
                                        />
                                        <input
                                          type="text" placeholder="Ex: Grade especial, Miúda…"
                                          value={s.descricao}
                                          onChange={(e) => updateSplit(c.k, t.k, idx, "descricao", e.target.value)}
                                          className="rounded-md bg-white border border-stone-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                                          data-testid={`split-desc-${t.k}-${c.k}-${idx}`}
                                        />
                                        <input
                                          type="number" step="0.01" min="0" placeholder="0,00"
                                          value={s.preco}
                                          onChange={(e) => updateSplit(c.k, t.k, idx, "preco", e.target.value)}
                                          className="rounded-md bg-white border border-stone-300 px-2 py-1.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-500"
                                          data-testid={`split-preco-${t.k}-${c.k}-${idx}`}
                                        />
                                        <div className="text-right text-sm font-mono text-ink-800 font-semibold" data-testid={`split-subtotal-${t.k}-${c.k}-${idx}`}>
                                          {fmtBRL(sub)}
                                        </div>
                                        <div className="text-right text-[11px] font-mono text-brand-600">
                                          {pS > 0 && Math.abs(efet - pS) > 0.005 ? `${fmtBRL(efet)}/sc` : "—"}
                                        </div>
                                        <button
                                          type="button"
                                          onClick={() => removeSplit(c.k, t.k, idx)}
                                          className="p-1 rounded hover:bg-red-100 text-danger justify-self-end"
                                          title="Remover sub-lote"
                                          data-testid={`split-remove-${t.k}-${c.k}-${idx}`}
                                        >
                                          <X size={14} />
                                        </button>
                                      </div>
                                    );
                                  })}
                                </div>
                                <div className="flex items-center justify-between pt-1 border-t border-brand-200">
                                  <button
                                    type="button"
                                    onClick={() => addSplit(c.k, t.k)}
                                    className="text-xs px-2 py-1 rounded bg-white border border-brand-300 text-brand-700 hover:bg-brand-50 flex items-center gap-1 font-semibold"
                                    data-testid={`split-add-${t.k}-${c.k}`}
                                  >
                                    <Plus size={12} /> Adicionar sub-lote
                                  </button>
                                  <div className="text-xs font-mono flex items-center gap-3">
                                    <span className={splitDiff !== 0 ? "text-warn font-semibold" : "text-ink-500"}>
                                      Somado: <strong>{fmtNum(qtdSplits, 0)}</strong> sc {splitDiff !== 0 && `(faltam ${fmtNum(splitDiff, 0)} sc)`}
                                    </span>
                                    <span className="text-brand-700 font-bold">
                                      Total: {fmtBRL(valorSplits)}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              /* MODO PREÇO ÚNICO — comportamento antigo */
                              <>
                                <input
                                  type="number" step="0.01" placeholder="0,00"
                                  value={form.precos[c.k]?.[t.k] ?? ""}
                                  onChange={(e) => setPreco(c.k, t.k, e.target.value)}
                                  disabled={qtd === 0}
                                  className={`w-full rounded-md border px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 ${
                                    qtd === 0
                                      ? "bg-stone-100 border-stone-200 text-ink-300 focus:ring-brand-500"
                                      : prejuizo
                                        ? "bg-red-50 border-danger text-danger focus:ring-red-500"
                                        : "bg-stone-50 border-stone-300 focus:ring-brand-500"
                                  }`}
                                  data-testid={`preco-${t.k}-${c.k}`}
                                />
                                {temAjuste && (
                                  <div
                                    className={`mt-1 text-[10px] font-mono leading-tight ${efetivoAcima ? "text-brand-600" : "text-warn"}`}
                                    data-testid={`efetivo-${t.k}-${c.k}`}
                                  >
                                    Mercadoria: <span className="font-bold">{fmtBRL(preco_efetivo)}</span>/sc
                                  </div>
                                )}
                                {custo > 0 && (
                                  <div
                                    className={`mt-0.5 text-[10px] font-mono flex items-center gap-1 leading-tight ${prejuizo ? "text-danger font-semibold" : "text-ink-400"}`}
                                    data-testid={`custo-${t.k}-${c.k}`}
                                  >
                                    {prejuizo && <AlertCircle size={10} className="shrink-0" />}
                                    <span>
                                      Custo: {fmtBRL(custo)}
                                      {preco > 0 && (
                                        <>
                                          {" · "}
                                          <span data-testid={`margem-${t.k}-${c.k}`}>
                                            {prejuizo ? "Prejuízo " : "Margem "}{fmtBRL(preco - custo)}
                                          </span>
                                        </>
                                      )}
                                    </span>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Cebola BRUTA (granel) — só aparece se há itens granel na saída */}
          {(() => {
            const granelItems = form.selectedItems.filter((it) => it.granel && Number(it.granel.quantidade) > 0);
            if (granelItems.length === 0) return null;
            const pesoTotalKg = granelItems.reduce((a, it) => a + Number(it.granel.quantidade || 0), 0);
            const precoKg = Number(form.precos.granel?.bruta) || 0;
            const valorTotal = pesoTotalKg * precoKg;
            return (
              <Card className="p-6 border-amber-300 bg-amber-50/40" data-testid="granel-block">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h3 className="font-heading text-lg font-bold text-ink-900">Cebola Bruta (granel)</h3>
                    <p className="text-xs text-ink-500">Preço por kg — saída sem sacaria, produto in natura a granel.</p>
                  </div>
                  <span className="px-2 py-1 rounded bg-amber-200 text-amber-900 text-[10px] font-bold uppercase">granel</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-[0.1em] text-ink-500 mb-1">Peso total</label>
                    <div className="font-mono font-bold text-xl text-ink-900">{pesoTotalKg.toLocaleString("pt-BR", { minimumFractionDigits: 2 })} kg</div>
                    <div className="text-[10px] text-ink-400">{granelItems.length} NF{granelItems.length > 1 ? "s" : ""}</div>
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-[0.1em] text-ink-500 mb-1">Preço R$/kg</label>
                    <input
                      type="number" step="0.01" min="0"
                      value={form.precos.granel?.bruta ?? ""}
                      onChange={(e) => setForm({ ...form, precos: { ...form.precos, granel: { ...form.precos.granel, bruta: e.target.value } } })}
                      className="w-full rounded-md bg-white border border-stone-300 px-3 py-2 text-lg font-mono focus:outline-none focus:ring-2 focus:ring-amber-500"
                      placeholder="0,00"
                      data-testid="granel-preco-kg"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-[0.1em] text-ink-500 mb-1">Valor da mercadoria</label>
                    <div className="font-mono font-bold text-2xl text-brand-600">{fmtBRL(valorTotal)}</div>
                  </div>
                  <div className="text-[11px] text-ink-500 leading-tight">
                    NFs a granel: {granelItems.map((it) => `#${it.nota_numero}`).join(", ")}
                  </div>
                </div>
              </Card>
            );
          })()}
          <Card className="p-6">
            <h3 className="font-heading text-lg font-bold text-ink-900 mb-1">Frete & ajustes</h3>
            <p className="text-[11px] text-ink-500 mb-4">
              Marque <span className="font-semibold">Aumentar</span> para acrescer ao valor da mercadoria; deixe desmarcado para descontar.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Select label="Frete" value={form.frete_tipo} onChange={(e) => setForm({ ...form, frete_tipo: e.target.value })}>
                <option value="FOB">FOB — pago pelo cliente</option>
                <option value="CIF">CIF — descontado do total</option>
              </Select>
              <FieldWithOp
                label="Valor do frete (R$)"
                value={form.valor_frete}
                onChange={(v) => setForm({ ...form, valor_frete: v })}
                op={form.frete_op}
                onOpChange={(op) => setForm({ ...form, frete_op: op })}
                testid="frete"
                hint={form.frete_tipo === "FOB" ? "FOB: geralmente pago pelo cliente — informe o valor apenas para registro/rastreio (não é somado nem descontado da carga)." : null}
              />
              <FieldWithOp
                label={form.descarga_mode === "saco" ? "Descarga (R$/saco)" : "Descarga (R$ total)"}
                value={form.descarga}
                onChange={(v) => setForm({ ...form, descarga: v })}
                op={form.descarga_op}
                onOpChange={(op) => setForm({ ...form, descarga_op: op })}
                testid="descarga"
                headerExtra={
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, descarga_mode: form.descarga_mode === "saco" ? "total" : "saco" })}
                    className="text-[10px] font-semibold text-ink-500 hover:text-brand-600 underline decoration-dotted"
                    data-testid="descarga-mode-toggle"
                    title="Alternar entre R$/saco e R$ total"
                  >
                    {form.descarga_mode === "saco" ? "usar total" : "usar /saco"}
                  </button>
                }
                hint={
                  form.descarga_mode === "saco" && (Number(form.descarga) || 0) > 0 && calc.total_caixas > 0
                    ? `${calc.total_caixas} sc × ${fmtBRL(Number(form.descarga) || 0)} = ${fmtBRL(calc.descarga_total)}`
                    : null
                }
              />
              <FieldWithOp
                label="Seguro (R$)"
                value={form.seguro}
                onChange={(v) => setForm({ ...form, seguro: v })}
                op={form.seguro_op}
                onOpChange={(op) => setForm({ ...form, seguro_op: op })}
                testid="seguro"
              />
              <FieldWithOp
                label="Desconto (%)"
                value={form.desconto_pct}
                onChange={(v) => setForm({ ...form, desconto_pct: v })}
                op={form.desconto_op}
                onOpChange={(op) => setForm({ ...form, desconto_op: op })}
                testid="desconto"
                suffix="%"
              />
              <div>
                <label className="block text-xs font-bold uppercase tracking-[0.1em] text-ink-500 mb-1">Vencimento</label>
                <div className="flex gap-1.5 mb-1.5">
                  {[15, 30, 45].map((d) => {
                    const target = (() => {
                      const dt = new Date();
                      dt.setDate(dt.getDate() + d);
                      return dt.toISOString().slice(0, 10);
                    })();
                    const active = form.data_vencimento === target;
                    return (
                      <button
                        key={d}
                        type="button"
                        onClick={() => setForm({ ...form, data_vencimento: target })}
                        data-testid={`venc-${d}d`}
                        className={`flex-1 text-xs px-2 py-1.5 rounded-md border font-semibold transition-colors ${
                          active
                            ? "bg-brand-600 border-brand-600 text-white"
                            : "bg-stone-50 border-stone-300 text-ink-700 hover:bg-brand-50 hover:border-brand-500"
                        }`}
                      >
                        {d} dias
                      </button>
                    );
                  })}
                </div>
                <input
                  type="date"
                  value={form.data_vencimento}
                  onChange={(e) => setForm({ ...form, data_vencimento: e.target.value })}
                  className="w-full rounded-md bg-stone-50 border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                  data-testid="shipment-data-vencimento"
                />
              </div>
            </div>
          </Card>

          {/* Vendedor & comissão */}
          <Card className="p-6">
            <h3 className="font-heading text-lg font-bold text-ink-900 mb-4">Vendedor & comissão</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="Vendedor"
                value={form.vendedor}
                onChange={(e) => setForm({ ...form, vendedor: e.target.value })}
                data-testid="shipment-vendedor"
              >
                <option value="">Sem vendedor</option>
                <option value="Marquinhos">Marquinhos</option>
                <option value="Boy">Boy</option>
                <option value="Allan">Allan</option>
                <option value="Matheus">Matheus</option>
              </Select>
              <Input
                label="Comissão (%)"
                type="number" step="0.01" min="0"
                value={form.comissao_pct}
                onChange={(e) => setForm({ ...form, comissao_pct: e.target.value })}
                placeholder="0,00"
                data-testid="shipment-comissao-pct"
              />
            </div>
            <p className="text-[11px] text-ink-500 mt-2">
              A comissão é calculada sobre o valor bruto e descontada do líquido da venda.
            </p>
          </Card>
        </div>

        {/* Resumo */}
        <div>
          <Card className="p-6 sticky top-20">
            <h3 className="font-heading font-bold text-ink-900 mb-4">Resumo</h3>
            <div className="space-y-2 text-sm">
              <Row label="Total de caixas" value={fmtNum(calc.total_caixas, 0)} />
              <Row label="Valor bruto" value={fmtBRL(calc.valor_bruto)} />
              {calc.cmv_total > 0 && (
                <>
                  <Row label="Custo da mercadoria" value={`- ${fmtBRL(calc.cmv_total)}`} tone="warn" />
                  <div className="flex justify-between items-baseline">
                    <span className="text-ink-500 text-xs">Margem bruta</span>
                    <span
                      className={`font-mono font-semibold text-xs ${calc.margem_bruta < 0 ? "text-danger" : "text-brand-600"}`}
                      data-testid="shipment-margem-bruta"
                    >
                      {calc.margem_bruta < 0 && <AlertCircle size={11} className="inline mr-0.5 -mt-0.5" />}
                      {fmtBRL(calc.margem_bruta)}
                    </span>
                  </div>
                </>
              )}
              <Row
                label={`${form.desconto_op === "add" ? "Acréscimo" : "Desconto"} (${form.desconto_pct || 0}%)`}
                value={`${calc.sig_desc >= 0 ? "+ " : "- "}${fmtBRL(calc.desconto)}`}
                tone={calc.sig_desc >= 0 ? "default" : "warn"}
              />
              {form.frete_tipo === "CIF" && (
                <Row
                  label={`Frete CIF${form.frete_op === "add" ? " (+)" : ""}`}
                  value={`${calc.sig_frete >= 0 ? "+ " : "- "}${fmtBRL(Number(form.valor_frete) || 0)}`}
                  tone={calc.sig_frete >= 0 ? "default" : "warn"}
                />
              )}
              {(Number(form.descarga) || 0) > 0 && (
                <Row
                  label={`Descarga${form.descarga_op === "add" ? " (+)" : ""}${form.descarga_mode === "saco" ? ` — ${fmtBRL(Number(form.descarga) || 0)}/sc` : ""}`}
                  value={`${calc.sig_descarga >= 0 ? "+ " : "- "}${fmtBRL(calc.descarga_total || 0)}`}
                />
              )}
              {(Number(form.seguro) || 0) > 0 && (
                <Row
                  label={`Seguro${form.seguro_op === "add" ? " (+)" : ""}`}
                  value={`${calc.sig_seguro >= 0 ? "+ " : "- "}${fmtBRL(Number(form.seguro) || 0)}`}
                />
              )}
              {(calc.comissao || 0) > 0 && <Row label={`Comissão${form.vendedor ? ` — ${form.vendedor}` : ""} (${form.comissao_pct || 0}%)`} value={`- ${fmtBRL(calc.comissao)}`} tone="warn" />}
              <hr className="border-ink-200 my-2" />
              <div className="flex justify-between items-baseline pt-1">
                <span className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500">Líquido</span>
                <span className="font-mono text-2xl font-bold text-brand-600" data-testid="shipment-liquido">{fmtBRL(calc.liquido)}</span>
              </div>
            </div>

            {calc.overflows.length > 0 && (
              <div className="mt-4 px-3 py-2 bg-red-50 border border-red-200 rounded-md text-xs text-danger space-y-0.5">
                <div className="flex items-center gap-1 font-semibold"><AlertCircle size={12} /> Estoque insuficiente:</div>
                {calc.overflows.map((o, i) => (
                  <div key={i} className="flex items-center justify-between gap-2">
                    <span>• {o.text}</span>
                    <button type="button" onClick={() => estornarBaixa(o)} className="shrink-0 px-2 py-0.5 rounded border border-red-300 bg-white hover:bg-red-100 font-semibold" data-testid={`estornar-baixa-${o.invoice_id}-${o.cat}`}>
                      Devolver ao estoque
                    </button>
                  </div>
                ))}
              </div>
            )}
            {calc.prejuizos.length > 0 && (
              <div
                className="mt-3 px-3 py-2 bg-red-50 border border-red-200 rounded-md text-xs text-danger space-y-0.5"
                data-testid="shipment-alerta-prejuizo"
              >
                <div className="flex items-center gap-1 font-semibold"><AlertCircle size={12} /> Venda abaixo do custo:</div>
                {calc.prejuizos.map((p, i) => <div key={i}>• {p}</div>)}
              </div>
            )}
            {calc.splitsWarnings && calc.splitsWarnings.length > 0 && (
              <div
                className="mt-3 px-3 py-2 bg-amber-50 border border-amber-300 rounded-md text-xs text-amber-900 space-y-0.5"
                data-testid="shipment-alerta-splits"
              >
                <div className="flex items-center gap-1 font-semibold"><AlertCircle size={12} /> Divisões incompletas:</div>
                {calc.splitsWarnings.map((w, i) => (
                  <div key={i}>• {w.label}: total {fmtNum(w.qtdBase, 0)} sc, mas dividido em {fmtNum(w.qtdSplits, 0)} sc ({w.diff > 0 ? "faltam" : "sobram"} {fmtNum(Math.abs(w.diff), 0)} sc)</div>
                ))}
              </div>
            )}
            {err && <div className="mt-3 text-sm text-danger bg-red-50 border border-red-200 px-3 py-2 rounded-md">{err}</div>}

            <Button onClick={save} size="lg" className="w-full mt-5" disabled={busy || calc.overflows.length > 0} data-testid="shipment-save-button">
              {busy ? <Spinner size={16} /> : (
                <><Save size={16} /> {isPendente && hasAnyPrice() ? "Finalizar Saída" : "Salvar rascunho"}</>
              )}
            </Button>
            <p className="text-[11px] text-ink-500 mt-2 text-center">
              {isPendente
                ? (hasAnyPrice() ? "Ao salvar, a saída será finalizada e a conta a receber gerada."
                                : "Preencha o cliente e ao menos um preço para finalizar.")
                : "Você poderá finalizar e imprimir após salvar."}
            </p>
          </Card>
        </div>
      </div>
    </Layout>
  );
}

function Row({ label, value, tone = "default" }) {
  const tones = { default: "text-ink-900", warn: "text-warn" };
  return (
    <div className="flex justify-between items-baseline">
      <span className="text-ink-500">{label}</span>
      <span className={`font-mono font-medium ${tones[tone]}`}>{value}</span>
    </div>
  );
}

function FieldWithOp({ label, value, onChange, op, onOpChange, testid, disabled = false, suffix = "R$", hint = null, headerExtra = null }) {
  const isAdd = op === "add";
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1 gap-2">
        <label className="block text-xs font-bold uppercase tracking-[0.1em] text-ink-500 truncate">{label}</label>
        <div className="flex items-center gap-2 shrink-0">
          {headerExtra}
          <label className="flex items-center gap-1 text-[10px] font-semibold text-ink-600 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={isAdd}
              disabled={disabled}
              onChange={(e) => onOpChange(e.target.checked ? "add" : "sub")}
              className="w-3.5 h-3.5 accent-brand-600 cursor-pointer"
              data-testid={`${testid}-op-toggle`}
            />
            <span className={isAdd ? "text-brand-600" : "text-ink-500"}>
              {isAdd ? "AUMENTAR (+)" : "diminuir (−)"}
            </span>
          </label>
        </div>
      </div>
      <input
        type="number" step="0.01"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        placeholder={suffix === "%" ? "0,00" : "0,00"}
        className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 ${
          disabled ? "bg-stone-100 border-stone-200 text-ink-300" : (isAdd ? "bg-emerald-50 border-brand-400" : "bg-stone-50 border-stone-300")
        }`}
        data-testid={`${testid}-input`}
      />
      {hint && <p className="text-[10px] text-ink-400 mt-0.5">{hint}</p>}
    </div>
  );
}
